<template>
	<main :data-test="$route.path">
		<header>
			<pageHeader1image v-bind="pageHeader">
				<button class="button button-goPink button-large space-x-12 group shadow-lg" @click.prevent="$scrollTo('team-members')">
					<span> Say Hi </span>
					<svg-icon name="arrow-forward" class="iconSize-24 opacity-50 translate-x-0 group-hover:translate-x-1" />
				</button>
			</pageHeader1image>

			<aboutUsHeader />

			<hr class="tablet:ml-48 desktop:ml-56 border-black/10" />
		</header>

		<div class="py-16 tablet:py-48 desktop:py-56 space-y-56 tabletWide:space-y-112">
			<LazyHydrate when-visible>
				<layoutSection>
					<layoutPost type="wide">
						<header>
							<h2 class="font-bold text-32 desktop:text-48 text-goPink">GO Rentals History</h2>
						</header>
						<p class="text-18 desktop:text-22 text-goGrayDarker">When GO Rentals was formed in 1997, our team was a small, tight-knit unit working out of one location. Fast forward to today, we've grown into a nationwide network with branches in 9 major New Zealand hubs and supported by a fantastic team spread throughout the country.</p>
						<p>Our team includes many people who have been here from day one and whilst we are always looking to grow and expand, it’s great to remind ourselves how far we have come.</p>
						<header>
							<h3 class="font-bold text-20 tablet:text-24">Our GO Rentals Business Values</h3>
						</header>
						<p>No matter which department our staff are working in, what we all have in common is our values.</p>
						<ul class="text-14 desktop:text-16">
								<li class="bg-iconChevron pl-32">
									Service first
								</li>
								<li class="bg-iconChevron pl-32">
									Mana Tangata (respecting each other)
								</li>
								<li class="bg-iconChevron pl-32">
									Be innovative
								</li>
								<li class="bg-iconChevron pl-32">
									Achieving excellence
								</li>
								<li class="bg-iconChevron pl-32">
									Whanaungatanga (working together)
								</li>
							</ul>
						<p>We're all working towards the same overall goal and we each bring our specialised skills to the table to make it happen, to compliment one another and to help each other grow. Every role is important to help us achieve a successful business.</p>
						<header>
							<h2 class="font-bold text-32 desktop:text-48 text-goPink">GO Rentals Management</h2>
						</header>
						<p class="text-18 desktop:text-22 text-goGrayDarker">Here’s a quick introduction to some members who have made GO Rentals what it is today.</p>
					</layoutPost>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection>
					<div scroll-target="team-members" class="p-16 tablet:p-0 grid grid-cols-12 gap-8 tablet:gap-16">
						<article v-for="(member, index) in allMembers" :key="index" class="group col-span-6 tabletWide:col-span-3 border border-goGrayBright/30 rounded-6 overflow-hidden shadow-sm hover:shadow cursor-pointer ease-linear delay-75 transition-shadow" @click="openTeamMember(member)">
							<figure>
								<goPicture v-bind="member.image" class="w-full h-full" />
							</figure>
							<div class="p-16 flex justify-between">
								<h2>
									<span class="text-14 tabletWide:text-16 font-bold">{{ member.name }}</span>
									<br>
									<span class="text-14 text-goGrayDarker">{{ member.role }}</span>
								</h2>
								<svg-icon name="arrow-forward" class="iconSize-24 text-goPink opacity-0 group-hover:opacity-100 ease-linear delay-75 transition-opacity" />
							</div>
						</article>
					</div>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection>
					<layoutPost type="wide">
						<header>
							<h2 class="text-24 tabletWide:text-28 font-bold">Want to join our team?</h2>
						</header>

						<footer>
							<nuxt-link to="/about-us/work-for-us/" class="button button-goBlueGhost button-normal">
								<span>Come work for us</span>
							</nuxt-link>
						</footer>
					</layoutPost>
				</layoutSection>
			</LazyHydrate>
		</div>
	</main>
</template>

<script>
import layoutSection from '~/components/layoutSection.vue'
import layoutPost from '~/components/layoutPost.vue'
import aboutUsHeader from '~/components/aboutUsHeader.vue'

export default {
	components: {
		layoutSection,
		layoutPost,
		aboutUsHeader,
		pageHeader1image: () => import("~/components/pageHeader1image.vue"),
	},
	metaInfo: {
		title: "Meet the Team - The GO Family - GO Rentals",
		meta: [
			{
				vmid: "description",
				name: "description",
				content: "Meet the Team at GO Rentals! Discover our GO business values, read about our team members' journey, and follow the growth of GO Rentals since 1997.",
			},
			{ vmid: "ogtitle", property: "og:title", content: "Meet the Team - The GO Family - GO Rentals" },
			{ vmid: "ogurl", property: "og:url", content: "https://www.gorentals.co.nz/about-us/our-team/" },
		],
		link: [{ rel: "canonical", href: "https://www.gorentals.co.nz/about-us/our-team/" }],
	},
	computed: {
		pageHeader() {
			return {
				title: "GO Rentals Management Team",
				headline: "Let's introduce you to the team at GO.",
				breadcrumb: { theme: "light", crumbs: [{ href: "/", text: "Home" }, { text: "About Us" }, { text: "Our Team" }] },
				picture: {
					alt: "Image of a female looking in the boot of a Hyundai Santa Fe rental car parked next to the sea",
					mobile: { src: "/images/OurTeam_Hero_Mobile.jpg", width: 375, height: 495 },
					tablet: { src: "/images/OurTeam_Hero_Tablet.jpg", width: 720, height: 720 },
					tabletWide: { src: "/images/OurTeam_Hero_Tablet_Wide.jpg", width: 976, height: 696 },
					desktop: { src: "/images/OurTeam_Hero_Desktop.jpg", width: 1384, height: 718 },
					desktopWide: { src: "/images/OurTeam_Hero_Desktop_Wide.jpg", width: 1864, height: 718 },
				},
			}
		},
		allMembers() {
			return [
				{
					name: "John Osborne",
					role: "Director",
					summary: "With over 35 years experience in the motor vehicle industry, I’ve seen it all. But it’s the team here at GO that have helped grow this business. My vision is to continually look for ways to expand on all of the great things we do… together!",
					comment: "“What I love about GO is the crew and the fact that we all sleep, eat and breathe service!”",
					image: {
						mobile: { src: "/images/JohnO_Desktop.jpg", width: 164, height: 164 },
						tablet: { src: "/images/JohnO_Desktop.jpg", width: 212, height: 212 },
						tabletWide: { src: "/images/JohnO_Desktop.jpg", width: 218, height: 218 },
						desktop: { src: "/images/JohnO_Desktop.jpg", width: 278, height: 278 },
						desktopWide: { src: "/images/JohnO_Desktop.jpg", width: 318, height: 318 },
						alt: "Head shot image of John Osborne, Director at GO Rentals",
					},
				},
				{
					name: "James Dalglish",
					role: "Chief Operating Officer",
					summary: "I’ve been with GO since the very early days and seen the business grow from strength to strength over the years. I love our industry and that golden opportunity we get, to give our customers an unforgettable experience here in Aotearoa!",
					comment: "“What I love about GO is the crew and the fact that we all sleep, eat and breathe service!”",
					image: {
						mobile: { src: "/images/James_Desktop.jpg", width: 164, height: 164 },
						tablet: { src: "/images/James_Desktop.jpg", width: 212, height: 212 },
						tabletWide: { src: "/images/James_Desktop.jpg", width: 218, height: 218 },
						desktop: { src: "/images/James_Desktop.jpg", width: 278, height: 278 },
						desktopWide: { src: "/images/James_Desktop.jpg", width: 318, height: 318 },
						alt: "Head shot image of James Dalglish, Chief Operating Officer at GO Rentals",
					},
				},
				{
					name: "Helen Osborne",
					role: "Head of People & Talent",
					summary: "Working within the HR team is an incredibly rewarding job at GO. It’s exciting working with such a fun and fabulous group of people who are passionate about what they do. Of course, when I’m not working it is either fashion or going to the gym that keeps me busy. Healthy body, healthy mind.",
					comment: "“What I love about GO is the crew and the fact that we all sleep, eat and breathe service!”",
					image: {
						mobile: { src: "/images/Helen_Desktop.jpg", width: 164, height: 164 },
						tablet: { src: "/images/Helen_Desktop.jpg", width: 212, height: 212 },
						tabletWide: { src: "/images/Helen_Desktop.jpg", width: 218, height: 218 },
						desktop: { src: "/images/Helen_Desktop.jpg", width: 278, height: 278 },
						desktopWide: { src: "/images/Helen_Desktop.jpg", width: 318, height: 318 },
						alt: "Head shot image of Helen Osborne, Head of People & Talent at GO Rentals",
					},
				},
				{
					name: "Kevin Dang",
					role: "IT Manager",
					summary: "I started off vehicle grooming here at GO after completing university. During my time at GO, there have been many opportunities for career development which I have grasped with both hands. I’m now the IT Manager and loving this challenge!",
					comment: "“What I love about GO is the crew and the fact that we all sleep, eat and breathe service!”",
					image: {
						mobile: { src: "/images/Kevin_Desktop.jpg", width: 164, height: 164 },
						tablet: { src: "/images/Kevin_Desktop.jpg", width: 212, height: 212 },
						tabletWide: { src: "/images/Kevin_Desktop.jpg", width: 218, height: 218 },
						desktop: { src: "/images/Kevin_Desktop.jpg", width: 278, height: 278 },
						desktopWide: { src: "/images/Kevin_Desktop.jpg", width: 318, height: 318 },
						alt: "Head shot image of Kevin Dang, IT Manager at GO Rentals",
					},
				},
				{
					name: "Teegan Huynh",
					role: "Chief Financial Officer",
					summary: "I started as CFO in February 2020 prior to the first NZ COVID lockdown. My personal passion for numbers and high performance has allowed me to navigate through the challenges of the Covid-19 pandemic. I am a qualified CA, and all about engaging through numbers and bringing this to life.",
					comment: "“What I love about GO is the crew and the fact that we all sleep, eat and breathe service!”",
					image: {
						mobile: { src: "/images/Teegan_Desktop.jpg", width: 164, height: 164 },
						tablet: { src: "/images/Teegan_Desktop.jpg", width: 212, height: 212 },
						tabletWide: { src: "/images/Teegan_Desktop.jpg", width: 218, height: 218 },
						desktop: { src: "/images/Teegan_Desktop.jpg", width: 278, height: 278 },
						desktopWide: { src: "/images/Teegan_Desktop.jpg", width: 318, height: 318 },
						alt: "Head shot image of Teegan Huynh, Chief Financial Officer at GO Rentals",
					},
				},
				{
					name: "Brad Rodden",
					role: "Operations Manager",
					summary: "I joined GO in 2007 and have worked in various roles across the company progressing through regional management into the Operations Manager role. I thrive on continuous improvement, developing staff and seeing them grow, and meeting people from all walks of life.",
					comment: "“What I love about GO is the crew and the fact that we all sleep, eat and breathe service!”",
					image: {
						mobile: { src: "/images/Brad_Desktop.jpg", width: 164, height: 164 },
						tablet: { src: "/images/Brad_Desktop.jpg", width: 212, height: 212 },
						tabletWide: { src: "/images/Brad_Desktop.jpg", width: 218, height: 218 },
						desktop: { src: "/images/Brad_Desktop.jpg", width: 278, height: 278 },
						desktopWide: { src: "/images/Brad_Desktop.jpg", width: 318, height: 318 },
						alt: "Head shot image of Brad Rodden, Operations Manager at GO Rentals",
					},
				},
				{
					name: "Clint Maskell",
					role: "Sales Manager",
					summary: "I’ve joined the GO team in March 2023, at an exciting time of growth and expansion. With a background in both sales and operations and over 25 years’ experience across automotive and tourism industries, I enjoy the fast-paced nature of growth business, while building strong teams and relationships.",
					comment: "“What I love about GO is the crew and the fact that we all sleep, eat and breathe service!”",
					image: {
						mobile: { src: "/images/Clint_Desktop.jpg", width: 164, height: 164 },
						tablet: { src: "/images/Clint_Desktop.jpg", width: 212, height: 212 },
						tabletWide: { src: "/images/Clint_Desktop.jpg", width: 218, height: 218 },
						desktop: { src: "/images/Clint_Desktop.jpg", width: 278, height: 278 },
						desktopWide: { src: "/images/Clint_Desktop.jpg", width: 318, height: 318 },
						alt: "Head shot image of Clint Maskell, Sales Manager at GO Rentals",
					},
				},
				{
					name: "Jeena Kalyan",
					role: "Marketing Manager",
					summary: "I am a qualified marketer skilled in leading key marketing functions across multiple channels. I love the fast-paced, creative, and ever-changing digital environment marketing entails. I’m passionate about growing brands and bringing them to life, increasing ROI, and discovering new opportunities.",
					comment: "“What I love about GO is the crew and the fact that we all sleep, eat and breathe service!”",
					image: {
						mobile: { src: "/images/Jeena_Desktop.jpg", width: 164, height: 164 },
						tablet: { src: "/images/Jeena_Desktop.jpg", width: 212, height: 212 },
						tabletWide: { src: "/images/Jeena_Desktop.jpg", width: 218, height: 218 },
						desktop: { src: "/images/Jeena_Desktop.jpg", width: 278, height: 278 },
						desktopWide: { src: "/images/Jeena_Desktop.jpg", width: 318, height: 318 },
						alt: "Head shot image of Jeena Kalyan, Marketing Manager at GO Rentals",
					},
				},
			]
		},
	},
	methods: {
		async openTeamMember(teamMember) {
			const { default: ourTeamModal } = await import("~/components/modals/about-OurTeamModal.vue")
			this.$modalService.show(ourTeamModal, teamMember)
		},
	},
};
</script>
